import { styled } from '@linaria/react';
import type { TFunction } from 'i18next';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DiffLineKind } from '../../../../../api/agent-miner-client/agent-miner-client.type';
import {
  inkDarkest,
  primaryLightest,
  secondaryBase,
  secondaryDarkest,
  secondaryLighter,
  secondaryLightest,
  skyDark,
  skyLight,
  skyLightest,
} from '../../../../../styles/design-tokens';
import { Tooltip } from '../../../../../ui-library/tooltip/tooltip';
import { LegendItem, LegendRow, LegendSwatch, NotTraced, VisuallyHidden } from '../pr-detail.helpers';
import { hunkLineBudget, lineAttributionKind } from '../pr-survival.helpers';
import {
  describeAttribution,
  rowBackground,
  rowBorder,
  signColor,
  tagBackground,
  tagBorderStyle,
} from './file-diff-view.helpers';
import type {
  PullRequestDiffHunk,
  PullRequestDiffLine,
  PullRequestFileLinesResponse,
} from '../../../../../api/agent-miner-client/agent-miner-client.type';
import type { LineAttributionDisplayKind } from '../pr-survival.helpers';

const VISIBLE_LINE_BUDGET = 400;
const HUNK_LINE_CAP = 300;

type FileDiffViewProps = {
  data: PullRequestFileLinesResponse;
  currentSessionId: string;
};

type DiffRowProps = {
  line: PullRequestDiffLine;
  currentSessionId: string;
  /**
   * Translator and the two static tag labels are hoisted out of the row so a
   * long diff does not run `useEffect`/context subscriptions once per line —
   * the parent reads them once and passes stable references down.
   */
  t: TFunction;
  tagAgent: string;
  tagAmbiguous: string;
};

const DiffRow = memo(function DiffRow({ line, currentSessionId, t, tagAgent, tagAmbiguous }: DiffRowProps) {
  const kind = lineAttributionKind(line, currentSessionId);
  const sign = line.kind === DiffLineKind.Add ? '+' : line.kind === DiffLineKind.Del ? '−' : '';
  const isAgent = kind.startsWith('agent');
  const description = isAgent ? describeAttribution(line, t) : '';
  const tagLabel = kind === 'agent-ambiguous' ? tagAmbiguous : tagAgent;

  return (
    <Row data-kind={line.kind} data-attribution={kind} $kind={line.kind} $attribution={kind}>
      <LineNumber>{line.old_line ?? ''}</LineNumber>
      <LineNumber>{line.new_line ?? ''}</LineNumber>
      <Sign $kind={line.kind}>{sign}</Sign>
      <Content>{line.content}</Content>
      <TagCell>
        {isAgent && (
          <>
            <Tooltip variant="black" label={description} multiline maw={320} withArrow>
              <Tag $ambiguous={kind === 'agent-ambiguous'}>{tagLabel}</Tag>
            </Tooltip>
            <VisuallyHidden>{description}</VisuallyHidden>
          </>
        )}
      </TagCell>
    </Row>
  );
});

type HunkBodyProps = {
  hunk: PullRequestDiffHunk;
  currentSessionId: string;
  t: TFunction;
  tagAgent: string;
  tagAmbiguous: string;
};

function HunkBody({ hunk, currentSessionId, t, tagAgent, tagAmbiguous }: HunkBodyProps) {
  const [showAll, setShowAll] = useState(false);
  const lines = showAll ? hunk.lines : hunk.lines.slice(0, HUNK_LINE_CAP);
  const remaining = hunk.lines.length - lines.length;

  return (
    <tbody>
      <HunkHeaderRow>
        <HunkHeaderCell colSpan={5}>{hunk.header}</HunkHeaderCell>
      </HunkHeaderRow>
      {lines.map((line) => (
        <DiffRow
          key={`${line.old_line ?? 'n'}-${line.new_line ?? 'n'}-${line.kind}`}
          line={line}
          currentSessionId={currentSessionId}
          t={t}
          tagAgent={tagAgent}
          tagAmbiguous={tagAmbiguous}
        />
      ))}
      {remaining > 0 && (
        <tr>
          <ExpandCell colSpan={5}>
            <ExpandButton type="button" aria-expanded={false} onClick={() => setShowAll(true)}>
              {t('agent_miner.pull_requests.code.expand_hunk', { count: remaining })}
            </ExpandButton>
          </ExpandCell>
        </tr>
      )}
    </tbody>
  );
}

/**
 * The merged diff for one PR file rendered as a table, with every added line
 * tagged by the agent edit that wrote it. Tags carry the turn and session as
 * a tooltip and as visually hidden text so the attribution is never hover- or
 * colour-only. Long files render a line budget up front and the rest on demand.
 *
 * @param {FileDiffViewProps} props - The file-lines payload and the viewed session id
 * @returns {JSX.Element} The tagged diff
 */
export function FileDiffView({ data, currentSessionId }: FileDiffViewProps) {
  const { t } = useTranslation();
  const [showHidden, setShowHidden] = useState(false);

  if (!data.patch_available) {
    return <NotTraced>{t('agent_miner.pull_requests.code.patch_unavailable')}</NotTraced>;
  }

  const legacyAttribution = data.attribution !== null && data.attribution.algorithm_version === 0;
  const { visible, hidden } = hunkLineBudget(data.hunks, VISIBLE_LINE_BUDGET);
  const hunks = showHidden ? data.hunks : visible;
  // Read the two static tag labels once and hand them (with `t`) to every row so
  // the labels are not re-resolved per line across a long diff.
  const tagAgent = t('agent_miner.pull_requests.code.tag_agent');
  const tagAmbiguous = t('agent_miner.pull_requests.code.tag_agent_ambiguous');

  return (
    <Wrap>
      {legacyAttribution && <NotTraced>{t('agent_miner.pull_requests.code.evidence_missing')}</NotTraced>}
      <LegendRow>
        <LegendItem>
          <LegendSwatch $color={secondaryBase} />
          {t('agent_miner.pull_requests.code.legend_current')}
        </LegendItem>
        <LegendItem>
          <LegendSwatch $color={secondaryLighter} />
          {t('agent_miner.pull_requests.code.legend_other')}
        </LegendItem>
        <LegendItem>
          <LegendSwatch $color={secondaryLightest} $dashed />
          {t('agent_miner.pull_requests.code.legend_ambiguous')}
        </LegendItem>
        <LegendItem>
          <LegendSwatch $color={primaryLightest} />
          {t('agent_miner.pull_requests.code.legend_human')}
        </LegendItem>
      </LegendRow>
      <TableScroll>
        {/* Mask source code from Sentry Session Replay: this table renders verbatim
            diff content and hunk headers. data-sentry-mask masks all descendants. */}
        <DiffTable data-sentry-mask>
          <caption>
            <VisuallyHidden>
              {data.filename} — {t('agent_miner.pull_requests.code.title')}
            </VisuallyHidden>
          </caption>
          {/* Under table-layout: fixed the browser reads column widths from these
              <col>s. The header row is position:absolute and the first in-flow
              row is a colSpan hunk header, so without this the per-cell widths on
              data rows are ignored and code text overflows the tag column. */}
          <colgroup>
            <col style={{ width: 44 }} />
            <col style={{ width: 44 }} />
            <col style={{ width: 16 }} />
            <col />
            <col style={{ width: 140 }} />
          </colgroup>
          <thead>
            <VisuallyHiddenRow>
              <th scope="col">{t('agent_miner.pull_requests.code.col_old')}</th>
              <th scope="col">{t('agent_miner.pull_requests.code.col_new')}</th>
              <th scope="col">{t('agent_miner.pull_requests.code.col_kind')}</th>
              <th scope="col">{t('agent_miner.pull_requests.code.col_content')}</th>
              <th scope="col">{t('agent_miner.pull_requests.code.col_attribution')}</th>
            </VisuallyHiddenRow>
          </thead>
          {hunks.map((hunk, index) => (
            <HunkBody
              key={`${hunk.header}-${index}`}
              hunk={hunk}
              currentSessionId={currentSessionId}
              t={t}
              tagAgent={tagAgent}
              tagAmbiguous={tagAmbiguous}
            />
          ))}
        </DiffTable>
      </TableScroll>
      {hidden.length > 0 && !showHidden && (
        <ExpandButton type="button" aria-expanded={false} onClick={() => setShowHidden(true)}>
          {t('agent_miner.pull_requests.code.show_more_hunks', { count: hidden.length })}
        </ExpandButton>
      )}
    </Wrap>
  );
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TableScroll = styled.div`
  overflow-x: auto;
  border: 1px solid ${skyLight};
  border-radius: 8px;
`;

const DiffTable = styled.table`
  border-collapse: collapse;
  /* Fixed layout so the browser sizes columns from the <colgroup> widths above
     instead of measuring every line — the win scales with diff length. Content
     (the auto <col>) takes the remainder; its long \`pre\` lines scroll inside
     TableScroll. */
  table-layout: fixed;
  width: 100%;
  min-width: 640px;
  font: 400 11.5px/1.6 'SF Mono', ui-monospace, Menlo, monospace;
`;

const VisuallyHiddenRow = styled.tr`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
`;

const HunkHeaderRow = styled.tr`
  background: ${skyLightest};
`;

const HunkHeaderCell = styled.td`
  padding: 4px 10px;
  color: ${skyDark};
  font-size: 11px;
  white-space: pre;
`;

const Row = styled.tr<{ $kind: PullRequestDiffLine['kind']; $attribution: LineAttributionDisplayKind }>`
  background: ${(props) => rowBackground(props.$kind, props.$attribution)};
  border-left: ${(props) => rowBorder(props.$attribution)};
`;

const LineNumber = styled.td`
  width: 44px;
  padding: 0 8px;
  text-align: right;
  color: ${skyDark};
  font-variant-numeric: tabular-nums;
  user-select: none;
  vertical-align: top;
`;

const Sign = styled.td<{ $kind: PullRequestDiffLine['kind'] }>`
  width: 16px;
  padding: 0 4px;
  text-align: center;
  color: ${(props) => signColor(props.$kind)};
  user-select: none;
  vertical-align: top;
`;

const Content = styled.td`
  padding: 0 10px;
  white-space: pre;
  tab-size: 4;
  color: ${inkDarkest};
`;

const TagCell = styled.td`
  width: 140px;
  padding: 0 8px;
  text-align: right;
  white-space: nowrap;
  vertical-align: top;
`;

const Tag = styled.span<{ $ambiguous: boolean }>`
  display: inline-block;
  padding: 1px 7px;
  border-radius: 99px;
  background: ${(props) => tagBackground(props.$ambiguous)};
  border: 1px ${(props) => tagBorderStyle(props.$ambiguous)} ${secondaryLighter};
  color: ${secondaryDarkest};
  font: 600 10px/1.4 'Figtree', sans-serif;
  cursor: default;
`;

const ExpandCell = styled.td`
  padding: 6px 10px;
  background: ${skyLightest};
`;

const ExpandButton = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  color: ${secondaryBase};
  font: 600 12px/1 'Figtree', sans-serif;
  cursor: pointer;
`;
